import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { StarRating, GoldBtn, Spinner } from '../components/UI';
import { getOTT } from '../lib/ottPlatforms';
import Navbar from '../components/Navbar';
import {
  getFilmRatings, upsertRating,
  getGenreVotes, upsertGenreVote, removeGenreVote,
  getReviews, insertReview, deleteReview,
  submitReport, saveOMDBFilm,
} from '../lib/supabase';
import { getOMDBFilm } from '../lib/omdb';

// ── Write Review popup ────────────────────────────────────────
function WriteReviewModal({ open, onClose, onSubmit, submitting }) {
  const [text, setText] = useState('');
  if (!open) return null;
  const handle = async () => {
    if (!text.trim()) return;
    await onSubmit(text.trim());
    setText('');
    onClose();
  };
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 300,
      background: 'rgba(4,4,6,0.88)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 14px',
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border2)',
        borderRadius: 14, padding: 'clamp(18px,4vw,28px)',
        width: '100%', maxWidth: 540,
        boxShadow: '0 32px 80px rgba(0,0,0,0.7)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text)', fontSize: 18, margin: 0 }}>Post Your Review</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>
        <textarea
          value={text} onChange={e => setText(e.target.value)} autoFocus
          placeholder="Share your honest thoughts about this film…"
          style={{
            width: '100%', height: 130, background: 'var(--surface2)',
            border: '1px solid var(--border2)', borderRadius: 8,
            color: 'var(--text)', padding: '12px 14px',
            fontSize: 16, resize: 'vertical',
            fontFamily: "'Crimson Text', serif", lineHeight: 1.6, outline: 'none',
          }}
          onFocus={e => e.target.style.borderColor = 'var(--gold-dim)'}
          onBlur={e => e.target.style.borderColor = 'var(--border2)'}
        />
        <div style={{ display: 'flex', gap: 10, marginTop: 12, justifyContent: 'flex-end' }}>
          <GoldBtn outline onClick={onClose}>Cancel</GoldBtn>
          <GoldBtn onClick={handle} disabled={submitting || !text.trim()}>
            {submitting ? 'Posting…' : 'Post Review'}
          </GoldBtn>
        </div>
      </div>
    </div>
  );
}

// ── Review card ───────────────────────────────────────────────
function ReviewItem({ review, currentUserId, onReport, onDelete }) {
  const [showReport, setShowReport] = useState(false);
  const [reason, setReason]         = useState('');
  const [reported, setReported]     = useState(false);
  const [deleting, setDeleting]     = useState(false);
  const isOwn = currentUserId && currentUserId === review.user_id;

  const submitRep = async () => {
    if (!reason.trim()) return;
    await onReport({ review, reason });
    setReported(true); setShowReport(false);
  };
  const del = async () => {
    if (!window.confirm('Delete your review? This cannot be undone.')) return;
    setDeleting(true);
    await onDelete(review.id);
  };

  return (
    <div style={{
      background: 'var(--surface)', borderRadius: 12, padding: '16px 18px',
      border: `1px solid ${isOwn ? 'rgba(212,175,55,0.18)' : 'var(--border)'}`,
      opacity: deleting ? 0.45 : 1, transition: 'opacity 0.2s',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, gap: 10 }}>
        {/* Left: avatar + name */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <div style={{
            width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
            background: isOwn
              ? 'linear-gradient(135deg, var(--gold-dim), var(--gold))'
              : 'linear-gradient(135deg,#1a1a28,#2a2a3a)',
            border: '1px solid var(--border2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: isOwn ? 'var(--black)' : 'var(--gold)', fontSize: 13, fontWeight: 700,
          }}>{(review.profiles?.username || 'A')[0].toUpperCase()}</div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontFamily: "'Playfair Display', serif", color: 'var(--gold)', fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {review.profiles?.username || 'Anonymous'}
              </span>
              {isOwn && <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: 'var(--gold-dim)', letterSpacing: 1, flexShrink: 0 }}>YOU</span>}
            </div>
            <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>
              {new Date(review.created_at).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
            </div>
          </div>
        </div>
        {/* Right: actions */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', flexShrink: 0 }}>
          {isOwn && (
            <button onClick={del} disabled={deleting} style={{
              background: 'none', border: '1px solid rgba(192,57,43,0.3)',
              color: 'var(--red)', borderRadius: 4, padding: '3px 8px',
              fontSize: 11, cursor: 'pointer',
            }}>{deleting ? '…' : '🗑'}</button>
          )}
          {!isOwn && currentUserId && !reported && (
            <button onClick={() => setShowReport(p => !p)} style={{
              background: 'none', border: '1px solid var(--border2)', color: 'var(--text-muted)',
              borderRadius: 4, padding: '3px 8px', fontSize: 11, cursor: 'pointer',
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor='var(--red)'; e.currentTarget.style.color='var(--red)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border2)'; e.currentTarget.style.color='var(--text-muted)'; }}
            >⚑</button>
          )}
          {reported && <span style={{ color: 'var(--green)', fontSize: 11 }}>✓</span>}
        </div>
      </div>

      <p style={{ color: 'var(--text)', fontSize: 15, lineHeight: 1.7, margin: 0 }}>{review.body}</p>

      {showReport && !reported && (
        <div style={{ marginTop: 12, padding: 12, background: 'rgba(192,57,43,0.06)', borderRadius: 8, border: '1px solid rgba(192,57,43,0.2)' }}>
          <p style={{ fontSize: 13, color: 'var(--text-dim)', marginBottom: 8 }}>Describe the issue:</p>
          <textarea value={reason} onChange={e => setReason(e.target.value)}
            placeholder="e.g. Spam, hate speech, personal attacks…"
            style={{ width: '100%', height: 64, background: 'var(--surface2)', border: '1px solid var(--border2)', borderRadius: 6, color: 'var(--text)', padding: '8px 10px', fontSize: 14, resize: 'none', fontFamily: "'Crimson Text', serif", outline: 'none' }}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <GoldBtn danger small onClick={submitRep}>Report</GoldBtn>
            <GoldBtn outline small onClick={() => setShowReport(false)}>Cancel</GoldBtn>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Read-only stars ───────────────────────────────────────────
function Stars({ value, max = 10, size = 18 }) {
  const filled = Math.round(value || 0);
  return (
    <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
      {Array.from({ length: max }, (_, i) => (
        <span key={i} style={{ fontSize: size, color: i < filled ? 'var(--gold)' : 'var(--border2)', lineHeight: 1 }}>★</span>
      ))}
    </div>
  );
}

// ── Film Page ─────────────────────────────────────────────────
export default function FilmPage({ film, onBack, onAuthOpen, onAdminClick, onChatClick, onBattleClick, onRankingsClick, onTasteClick }) {
  const { user, profile, banned } = useAuth();
  const [ratings,    setRatings]    = useState([]);
  const [myRating,   setMyRating]   = useState(0);
  const [genreVotes, setGenreVotes] = useState([]);
  const [myGenreVotes,setMyGenreVotes]= useState([]);
  const [reviews,    setReviews]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [omdbData, setOmdbData] = useState(null);
  const [savedFilmId, setSavedFilmId] = useState(film._fromOMDB ? null : film.id);

  const load = useCallback(async () => {
    if (!film) return;
    const fid = savedFilmId;
    if (!fid) { setLoading(false); return; } // OMDB film not yet saved
    setLoading(true);
    const [r, gv, rev] = await Promise.all([
      getFilmRatings(fid),
      getGenreVotes(fid),
      getReviews(fid),
    ]);
    setRatings(r); setGenreVotes(gv); setReviews(rev);
    if (user) {
      setMyRating(r.find(x => x.user_id === user.id)?.rating || 0);
      setMyGenreVotes(gv.filter(x => x.user_id === user.id).map(x => x.genre));
    }
    setLoading(false);
  }, [film, user, savedFilmId]);

  // Load full OMDB data if this is an OMDB film
  useEffect(() => {
    if (film._fromOMDB && film.imdb_id) {
      getOMDBFilm(film.imdb_id).then(data => {
        if (data) setOmdbData(data);
      });
    }
    window.scrollTo(0, 0);
  }, [film]);

  useEffect(() => { load(); }, [load]);

  if (!film) return null;

  const hasPoster  = film.poster_url && film.poster_url.startsWith('http');
  const avgRating  = ratings.length ? ratings.reduce((s, x) => s + x.rating, 0) / ratings.length : null;
  const genreMap   = {};
  genreVotes.forEach(({ genre }) => { genreMap[genre] = (genreMap[genre] || 0) + 1; });
  const totalGV    = Object.values(genreMap).reduce((a, b) => a + b, 0);

  const getOrSaveFilmId = async () => {
    if (savedFilmId) return savedFilmId;
    if (film._fromOMDB) {
      const newId = await saveOMDBFilm(film);
      if (newId) { setSavedFilmId(newId); return newId; }
    }
    return film.id;
  };

  const handleRate = async n => {
    if (!user) return onAuthOpen();
    if (banned) return alert('Your account is currently suspended.');
    setMyRating(n);
    const fid = await getOrSaveFilmId();
    if (!fid) return;
    await upsertRating(fid, user.id, n);
    setRatings(await getFilmRatings(fid));
  };
  const handleVote = async genre => {
    if (!user) return onAuthOpen();
    if (banned) return alert('Your account is currently suspended.');
    const alreadyVoted = myGenreVotes.includes(genre);
    if (alreadyVoted) {
      // Toggle off — remove vote
      setMyGenreVotes(prev => prev.filter(g => g !== genre));
      const fid2 = await getOrSaveFilmId(); if (fid2) await removeGenreVote(fid2, user.id, genre);
    } else {
      // Add vote
      setMyGenreVotes(prev => [...prev, genre]);
      const fid3 = await getOrSaveFilmId(); if (fid3) await upsertGenreVote(fid3, user.id, genre);
    }
    setGenreVotes(await getGenreVotes(film.id));
  };
  const handleReview = async text => {
    if (banned) return alert('Your account is currently suspended.');
    setSubmitting(true);
    const fid4 = await getOrSaveFilmId(); if (!fid4) return; await insertReview(fid4, user.id, text);
    setReviews(await getReviews(film.id));
    setSubmitting(false);
  };
  const handleDelete = async reviewId => {
    await deleteReview(reviewId, user.id);
    setReviews(await getReviews(film.id));
  };
  const handleReport = async ({ review, reason }) => {
    await submitReport({ reviewId: review.id, reportedUserId: review.user_id, reportedBy: user.id, filmId: film.id, reason });
  };

  return (
    <>
      <style>{`
        .fp-ratings-grid { grid-template-columns: 1fr 1fr; }
        @media (max-width: 500px) {
          .fp-ratings-grid { grid-template-columns: 1fr !important; }
          .fp-content { padding: 0 14px 60px !important; }
          .fp-poster-img { max-height: 300px !important; }
          .fp-poster-pad { padding: 24px 14px 0 !important; min-height: 300px !important; }
        }
      `}</style>

      <div style={{ minHeight: '100vh', background: 'var(--black)' }}>
        <Navbar onBack={onBack} onAuthOpen={onAuthOpen} onAdminClick={onAdminClick} onChatClick={onChatClick}
        onBattleClick={onBattleClick}
        onRankingsClick={onRankingsClick}
        onTasteClick={onTasteClick} />

        {/* ── HERO POSTER ── */}
        <div style={{ position: 'relative', width: '100%', overflow: 'hidden' }}>
          {hasPoster ? (
            <>
              <div style={{
                position: 'absolute', inset: 0,
                backgroundImage: `url(${film.poster_url})`,
                backgroundSize: 'cover', backgroundPosition: 'center',
                filter: 'blur(28px) brightness(0.3)', transform: 'scale(1.1)',
              }} />
              <div className="fp-poster-pad" style={{ position: 'relative', display: 'flex', justifyContent: 'center', padding: '36px 20px 0', minHeight: 380 }}>
                <img src={film.poster_url} alt={film.title}
                  className="fp-poster-img"
                  style={{ maxHeight: 400, maxWidth: '100%', objectFit: 'contain', borderRadius: 10, boxShadow: '0 20px 70px rgba(0,0,0,0.8)', position: 'relative', zIndex: 1 }}
                />
              </div>
            </>
          ) : (
            <div style={{ minHeight: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(180deg,#0d0d18,var(--black))', fontSize: 80 }}>
              {film.poster_emoji || '🎬'}
            </div>
          )}
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 100, background: 'linear-gradient(to top, var(--black) 0%, transparent 100%)' }} />
        </div>

        {/* ── CONTENT ── */}
        <div className="fp-content" style={{ maxWidth: 820, margin: '0 auto', padding: '0 20px 80px' }}>

          {/* Title + meta */}
          <div style={{ marginBottom: 24, paddingTop: 6 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: 'var(--gold)', letterSpacing: 2, textTransform: 'uppercase', background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.2)', borderRadius: 4, padding: '3px 8px' }}>
                {film.language}
              </span>
              {film.year && <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{film.year}</span>}
              {film.imdb_rating && (
                <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                  IMDb <span style={{ color: 'var(--gold-dim)', fontFamily: "'DM Mono', monospace" }}>{film.imdb_rating}</span>/10
                </span>
              )}
            </div>
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(24px,5vw,44px)', fontWeight: 900, color: 'var(--text)', lineHeight: 1.15, margin: '0 0 14px' }}>
              {film.title}
            </h1>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
              {(film.genres || []).map(g => (
                <span key={g} style={{ background: 'var(--surface2)', border: '1px solid var(--border2)', borderRadius: 5, padding: '3px 9px', fontSize: 12, color: 'var(--text-dim)' }}>{g}</span>
              ))}
            </div>
            {film.description && (
              <p style={{ color: 'var(--text-dim)', fontSize: 'clamp(14px,2vw,16px)', lineHeight: 1.75 }}>{film.description}</p>
            )}

            {/* Extra OMDB details — director, runtime, awards */}
            {(film.director || film.runtime || film.awards || omdbData) && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 16 }}>
                {(film.director || omdbData?.Director) && omdbData?.Director !== 'N/A' && (
                  <div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: "'DM Mono', monospace", letterSpacing: 1.5, marginBottom: 3 }}>DIRECTOR</div>
                    <div style={{ color: 'var(--text)', fontSize: 14 }}>{film.director || omdbData?.Director}</div>
                  </div>
                )}
                {(film.runtime || omdbData?.Runtime) && omdbData?.Runtime !== 'N/A' && (
                  <div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: "'DM Mono', monospace", letterSpacing: 1.5, marginBottom: 3 }}>RUNTIME</div>
                    <div style={{ color: 'var(--text)', fontSize: 14 }}>{film.runtime || omdbData?.Runtime}</div>
                  </div>
                )}
                {(film.country || omdbData?.Country) && omdbData?.Country !== 'N/A' && (
                  <div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: "'DM Mono', monospace", letterSpacing: 1.5, marginBottom: 3 }}>COUNTRY</div>
                    <div style={{ color: 'var(--text)', fontSize: 14 }}>{film.country || omdbData?.Country}</div>
                  </div>
                )}
              </div>
            )}

            {/* Awards */}
            {(film.awards || (omdbData?.Awards && omdbData.Awards !== 'N/A')) && (
              <div style={{ marginTop: 14, background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.15)',
                borderRadius: 8, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 16 }}>🏆</span>
                <span style={{ color: 'var(--gold-dim)', fontSize: 13 }}>{film.awards || omdbData?.Awards}</span>
              </div>
            )}

            {/* Cast section */}
            {((film.film_cast && film.film_cast.length > 0) || (omdbData?.Actors && omdbData.Actors !== 'N/A')) && (
              <div style={{ marginTop: 20 }}>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: "'DM Mono', monospace", letterSpacing: 1.5, marginBottom: 10 }}>CAST</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {(film.film_cast?.length > 0 ? film.film_cast : (omdbData?.Actors || '').split(',').map(a => a.trim())).map((actor, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8,
                      background: 'var(--surface2)', border: '1px solid var(--border)',
                      borderRadius: 20, padding: '6px 12px' }}>
                      <div style={{ width: 26, height: 26, borderRadius: '50%', background: `hsl(${actor.charCodeAt(0) * 37 % 360}, 40%, 35%)`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                        {actor[0]?.toUpperCase()}
                      </div>
                      <span style={{ color: 'var(--text)', fontSize: 13 }}>{actor}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* OMDb credit */}
            {film._fromOMDB && (
              <div style={{ marginTop: 14, color: 'var(--text-muted)', fontSize: 11,
                fontFamily: "'DM Mono', monospace" }}>
                Data provided by <span style={{ color: 'var(--gold-dim)' }}>OMDb API</span>
              </div>
            )}
          </div>

          {/* OTT */}
          {film.ott_platforms?.length > 0 && (
            <div style={{ marginBottom: 28 }}>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: 'var(--text-muted)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 10 }}>Available On</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {film.ott_platforms.map(id => {
                  const p = getOTT(id);
                  if (!p) return null;
                  return (
                    <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 8, background: p.bgColor, border: `1px solid ${p.color}55`, borderRadius: 9, padding: '7px 14px' }}>
                      <span style={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }} dangerouslySetInnerHTML={{ __html: p.logo }} />
                      <span style={{ color: p.color, fontSize: 14, fontWeight: 600 }}>{p.name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div style={{ height: 1, background: 'linear-gradient(to right, transparent, var(--border2), transparent)', marginBottom: 28 }} />

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 50 }}><Spinner size={32} /></div>
          ) : (
            <>
              {/* Ratings */}
              <div className="fp-ratings-grid" style={{ display: 'grid', gap: 14, marginBottom: 28 }}>
                <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '18px 20px' }}>
                  <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: 'var(--text-muted)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>Critiqr Score</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, marginBottom: 4 }}>
                    <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 46, color: 'var(--gold)', fontWeight: 900, lineHeight: 1 }}>
                      {avgRating ? avgRating.toFixed(1) : '—'}
                    </span>
                    <span style={{ color: 'var(--text-muted)', fontSize: 16 }}>/10</span>
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 12 }}>from {ratings.length} rating{ratings.length !== 1 ? 's' : ''}</div>
                  {avgRating && <Stars value={avgRating} size={16} />}
                </div>

                <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '18px 20px' }}>
                  <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: 'var(--text-muted)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>Your Rating</div>
                  {user ? (
                    <>
                      <div style={{ color: 'var(--text-dim)', fontSize: 13, marginBottom: 10 }}>
                        {myRating ? <span>You rated <strong style={{ color: 'var(--gold)' }}>{myRating}/10</strong></span> : 'Tap to rate:'}
                      </div>
                      <StarRating value={myRating} onChange={handleRate} size={22} />
                    </>
                  ) : (
                    <div>
                      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 12 }}>Log in to rate this film.</p>
                      <GoldBtn small onClick={onAuthOpen}>Log In to Rate</GoldBtn>
                    </div>
                  )}
                </div>
              </div>

              {/* Genre Votes */}
              <div style={{ marginBottom: 28 }}>
                <h3 style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text)', fontSize: 18, fontWeight: 700, marginBottom: 12 }}>Genre Votes</h3>
                {!user ? (
                  <div style={{ padding: '14px 18px', background: 'var(--surface)', border: '1px dashed var(--border2)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                    <span style={{ color: 'var(--text-muted)', fontSize: 14 }}>Log in to vote on genres.</span>
                    <GoldBtn small onClick={onAuthOpen}>Log In</GoldBtn>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {(film.genres || []).map(genre => {
                      const votes  = genreMap[genre] || 0;
                      const pct    = totalGV ? Math.round(votes / totalGV * 100) : 0;
                      const active = myGenreVotes.includes(genre);
                      return (
                        <button key={genre} onClick={() => handleVote(genre)} style={{
                          background: active ? 'rgba(212,175,55,0.13)' : 'var(--surface)',
                          border: `1.5px solid ${active ? 'var(--gold)' : 'var(--border2)'}`,
                          color: active ? 'var(--gold)' : 'var(--text-dim)',
                          borderRadius: 8, padding: '7px 13px', fontSize: 13,
                          cursor: 'pointer', transition: 'all 0.18s', fontFamily: "'Crimson Text', serif",
                        }}>
                          {genre}
                          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, marginLeft: 7, opacity: 0.65 }}>
                            {votes} · {pct}%
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div style={{ height: 1, background: 'linear-gradient(to right, transparent, var(--border2), transparent)', marginBottom: 28 }} />

              {/* Reviews header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, flexWrap: 'wrap', gap: 10 }}>
                <div>
                  <h3 style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text)', fontSize: 22, fontWeight: 700, margin: '0 0 2px' }}>Reviews</h3>
                  <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>{reviews.length} review{reviews.length !== 1 ? 's' : ''}</div>
                </div>
                {user
                  ? <GoldBtn onClick={() => setShowReviewModal(true)}>✍ Post Your Review</GoldBtn>
                  : <GoldBtn outline onClick={onAuthOpen}>Log In to Review</GoldBtn>
                }
              </div>

              {/* Reviews list */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {reviews.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 20px', background: 'var(--surface)', borderRadius: 12, border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 36, marginBottom: 10 }}>🎭</div>
                    <div style={{ color: 'var(--text-dim)', fontSize: 16, marginBottom: 14 }}>No reviews yet. Be the first.</div>
                    {user
                      ? <GoldBtn onClick={() => setShowReviewModal(true)}>Write the First Review</GoldBtn>
                      : <GoldBtn outline onClick={onAuthOpen}>Log In to Review</GoldBtn>
                    }
                  </div>
                ) : reviews.map(r => (
                  <ReviewItem key={r.id} review={r} currentUserId={user?.id} onReport={handleReport} onDelete={handleDelete} />
                ))}
              </div>
            </>
          )}
        </div>

        <WriteReviewModal open={showReviewModal} onClose={() => setShowReviewModal(false)} onSubmit={handleReview} submitting={submitting} />
      </div>
    </>
  );
}
